/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

/**
 * PRIORITY QUEUE METRICS
 *
 * Agents who try to claim a User within 5 minutes of THEIR notification and get
 * beaten to it earn a priority tier — they're notified 10 min earlier per tier on
 * the next User (max 3 tiers = 30 min head start). Priority resets to 0 the moment
 * they land a User, and decays one tier per 30 idle days.
 *
 * This panel exists to answer two tuning questions:
 *
 *   1. IS THE TOP TIER CROWDING?
 *      If half the pool sits at tier 3, the head start buys nobody anything and
 *      the tiers need finer resolution (ordering within a tier by grievance age).
 *
 *   2. IS 5 MINUTES THE RIGHT WINDOW?
 *      If lost claims cluster just ABOVE 300s, good-faith agents are missing the
 *      cutoff by seconds and earning nothing. Widen it.
 */

const PRESETS = [
  { label: '7d',  days: 7  },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const TIER_META = {
  3: { label: 'Tier 3', sub: '30m head start', color: '#1e3a8a' },
  2: { label: 'Tier 2', sub: '20m head start', color: '#1d4ed8' },
  1: { label: 'Tier 1', sub: '10m head start', color: '#3b82f6' },
  0: { label: 'Tier 0', sub: 'no head start',  color: '#cbd5e1' },
};

const fmt = (n) => (n == null ? '—' : n.toLocaleString());

const fmtSeconds = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
};

const ATTEMPT_FILTERS = [
  { key: 'all',     label: 'All attempts' },
  { key: 'failed',  label: 'Beaten to it' },
  { key: 'success', label: 'Claimed' },
];

const WINDOW_FILTERS = [
  { key: 'all',     label: 'Any time' },
  { key: 'inside',  label: 'Within 5 min' },
  { key: 'outside', label: 'Outside 5 min' },
];

const PriorityQueuePanel = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Claim attempt log
  const [attempts, setAttempts] = useState([]);
  const [attemptSummary, setAttemptSummary] = useState(null);
  const [attemptsLoading, setAttemptsLoading] = useState(true);
  const [attemptPage, setAttemptPage] = useState(1);
  const [attemptsHasMore, setAttemptsHasMore] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [windowFilter, setWindowFilter] = useState('all');

  // Tier drill-down: which agents are actually sitting in a tier. Counts alone
  // don't answer "who is stuck at 3", which is the question that follows.
  const [openTier, setOpenTier] = useState(null);
  const [tierMembers, setTierMembers] = useState({});
  const [tierLoading, setTierLoading] = useState(false);

  const fetchTierMembers = async (tier) => {
    // Toggle closed if it's already open.
    if (openTier === tier) { setOpenTier(null); return; }
    setOpenTier(tier);
    if (tierMembers[tier]) return; // cached from a previous open

    setTierLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/priority/tier/${tier}`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      const json = await res.json();
      if (json.success) {
        setTierMembers(prev => ({ ...prev, [tier]: json.data.agents }));
      }
    } catch (err) {
      console.error('Error fetching tier members:', err);
    } finally {
      setTierLoading(false);
    }
  };

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/priority/overview?days=${days}`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
      } else {
        setError(json.message || 'Failed to load priority metrics');
      }
    } catch (err) {
      setError('Error loading priority metrics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchAttempts = useCallback(async (page = 1, append = false) => {
    if (!append) setAttemptsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '25',
        days: String(days),
        outcome: outcomeFilter,
        window: windowFilter,
      });
      const res = await fetch(`${API_URL}/admin/priority/attempts?${params}`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      const json = await res.json();
      if (json.success) {
        setAttempts(prev => (append ? [...prev, ...json.data] : json.data));
        setAttemptSummary(json.summary);
        setAttemptPage(page);
        setAttemptsHasMore(json.pagination?.hasMore || false);
      }
    } catch (err) {
      console.error('Error loading claim attempts', err);
    } finally {
      setAttemptsLoading(false);
    }
  }, [days, outcomeFilter, windowFilter]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchAttempts(1); }, [fetchAttempts]);

  const dist = data?.tierDistribution || { 0: 0, 1: 0, 2: 0, 3: 0 };
  const totalAgents = data?.totalAgents || 0;
  const withPriority = data?.agentsWithPriority || 0;
  const fc = data?.failedClaims || {};
  const p = fc.responseSeconds || {};

  // The headline tuning signal: of everyone who tried and lost, what share moved
  // fast enough to earn a tier? A low number means the window is too tight.
  const insideRate = fc.total > 0
    ? Math.round((fc.insideWindow / fc.total) * 100)
    : null;

  // Crowding check. If most agents holding priority are at the ceiling, the head
  // start stops differentiating anyone.
  const atCeiling = dist[3] || 0;
  const ceilingRate = withPriority > 0
    ? Math.round((atCeiling / withPriority) * 100)
    : 0;

  const maxTierCount = Math.max(1, ...[0, 1, 2, 3].map(t => dist[t] || 0));

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Priority Queue — matches the Control Center design system */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-bottom: 16px;
        }
        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .admin-panel .card-note {
          font-size: 12px;
          color: #9ca3af;
          line-height: 1.5;
          margin: -4px 0 16px;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .btn {
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #6b7280;
          font-family: inherit;
        }
        .admin-panel .btn:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .admin-panel .btn:disabled { opacity: 0.6; cursor: default; }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          color: #fff;
        }

        .admin-panel .preset-chip {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }
        .admin-panel .preset-chip:hover { background: #f9fafb; color: #111; }
        .admin-panel .preset-chip.on {
          background: rgba(37, 99, 235, 0.08);
          border-color: rgba(37, 99, 235, 0.35);
          color: #1d4ed8;
        }

        /* Summary tiles */
        .admin-panel .stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 720px) {
          .admin-panel .stat-grid { grid-template-columns: 1fr; }
        }
        .admin-panel .stat-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 12px;
          padding: 18px;
        }
        .admin-panel .stat-tile.primary {
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.06), rgba(37, 99, 235, 0.02));
          border-color: rgba(37, 99, 235, 0.18);
        }
        .admin-panel .stat-tile.warn {
          background: linear-gradient(135deg, rgba(220, 38, 38, 0.05), rgba(220, 38, 38, 0.02));
          border-color: rgba(220, 38, 38, 0.18);
        }
        .admin-panel .stat-label {
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .admin-panel .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.02em;
          line-height: 1.1;
          font-variant-numeric: tabular-nums;
        }
        .admin-panel .stat-sub {
          font-size: 12px;
          color: #9ca3af;
          margin-top: 6px;
          font-weight: 500;
        }

        /* Mini tiles */
        .admin-panel .mini-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px;
        }
        .admin-panel .mini-tile {
          position: relative;
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 10px;
          padding: 14px;
        }
        .admin-panel .mini-tile .mini-label {
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .admin-panel .mini-tile .mini-value {
          font-size: 20px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.01em;
          line-height: 1.1;
          font-variant-numeric: tabular-nums;
        }

        /* Tier distribution bars */
        .admin-panel .bar-row {
          display: grid;
          grid-template-columns: 160px 1fr 60px;
          gap: 12px;
          align-items: center;
          padding: 9px 0;
          border-bottom: 1px solid #f5f5f5;
        }
        .admin-panel .bar-row:last-child { border-bottom: none; }
        .admin-panel .bar-label {
          font-size: 13px;
          font-weight: 600;
          color: #111;
        }
        .admin-panel .bar-label span {
          display: block;
          font-size: 11px;
          font-weight: 500;
          color: #9ca3af;
          margin-top: 1px;
        }
        .admin-panel .bar-track {
          height: 8px;
          background: #f3f4f6;
          border-radius: 999px;
          overflow: hidden;
        }
        .admin-panel .bar-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.4s ease;
        }
        .admin-panel .bar-value {
          font-size: 13px;
          font-weight: 700;
          color: #111;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        /* Percentile strip */
        .admin-panel .pct-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }
        .admin-panel .pct-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 10px;
          padding: 14px;
          text-align: center;
        }
        .admin-panel .pct-tile.over {
          background: rgba(220, 38, 38, 0.05);
          border-color: rgba(220, 38, 38, 0.2);
        }
        .admin-panel .pct-tile .pct-label {
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .admin-panel .pct-tile .pct-value {
          font-size: 18px;
          font-weight: 700;
          color: #111;
          font-variant-numeric: tabular-nums;
        }
        .admin-panel .pct-tile.over .pct-value { color: #dc2626; }

        .admin-panel .verdict {
          margin-top: 14px;
          padding: 12px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.5;
        }
        .admin-panel .verdict.ok {
          background: rgba(22, 163, 74, 0.06);
          border: 1px solid rgba(22, 163, 74, 0.2);
          color: #15803d;
        }
        .admin-panel .verdict.warn {
          background: rgba(217, 119, 6, 0.06);
          border: 1px solid rgba(217, 119, 6, 0.2);
          color: #b45309;
        }


        /* Claim attempt log */
        .admin-panel .attempt-row {
          display: grid;
          grid-template-columns: 1.4fr 1.3fr 90px 110px 1fr;
          gap: 12px;
          align-items: center;
          padding: 11px 0;
          border-bottom: 1px solid #f5f5f5;
          font-size: 13px;
        }
        .admin-panel .attempt-row:last-child { border-bottom: none; }
        .admin-panel .attempt-head {
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          border-bottom: 1px solid #ececec;
          padding-bottom: 8px;
        }
        .admin-panel .attempt-agent {
          font-weight: 600;
          color: #111;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .admin-panel .attempt-agent span {
          display: block;
          font-size: 11px;
          font-weight: 500;
          color: #9ca3af;
        }
        .admin-panel .attempt-lead {
          color: #6b7280;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .admin-panel .attempt-lead span {
          display: block;
          font-size: 11px;
          color: #c7c9cd;
        }
        .admin-panel .pill {
          display: inline-block;
          padding: 3px 9px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .admin-panel .pill.won {
          background: rgba(22, 163, 74, 0.1);
          color: #15803d;
        }
        .admin-panel .pill.lost {
          background: rgba(220, 38, 38, 0.1);
          color: #b91c1c;
        }
        .admin-panel .attempt-time {
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          color: #111;
        }
        .admin-panel .attempt-time.earned { color: #2563eb; }
        .admin-panel .attempt-time.missed { color: #c7c9cd; }
        .admin-panel .attempt-time span {
          display: block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .admin-panel .attempt-when {
          font-size: 12px;
          color: #9ca3af;
          text-align: right;
        }
        .admin-panel .empty-row {
          padding: 28px 0;
          text-align: center;
          color: #9ca3af;
          font-size: 13px;
        }
        @media (max-width: 900px) {
          .admin-panel .attempt-row { grid-template-columns: 1fr 80px 100px; }
          .admin-panel .attempt-lead, .admin-panel .attempt-when { display: none; }
        }

        .admin-panel .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #fafafa 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: skel 1.4s linear infinite;
          border-radius: 6px;
          color: transparent;
        }
        @keyframes skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{
          fontSize: '11px', fontWeight: '600',
          letterSpacing: '0.08em', color: '#888',
          textTransform: 'uppercase'
        }}>
          Priority Queue
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {/* Controls */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Window</h3>
          <button
            className="btn btn-primary"
            onClick={() => { fetchOverview(); fetchAttempts(1); }}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {PRESETS.map(preset => (
            <button
              key={preset.label}
              className={`preset-chip ${days === preset.days ? 'on' : ''}`}
              onClick={() => setDays(preset.days)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Headline */}
      <div className="dashboard-card">
        <div className="card-header"><h3>Overview</h3></div>
        <div className="stat-grid">
          <div className="stat-tile primary">
            <div className="stat-label">Agents With Priority</div>
            <div className={`stat-value ${loading ? 'skeleton' : ''}`}>
              {fmt(withPriority)}
            </div>
            <div className="stat-sub">
              of {fmt(totalAgents)} agents · {totalAgents > 0 ? Math.round((withPriority / totalAgents) * 100) : 0}%
            </div>
          </div>

          <div className="stat-tile warn">
            <div className="stat-label">Beaten To It</div>
            <div className={`stat-value ${loading ? 'skeleton' : ''}`}>
              {fmt(fc.total)}
            </div>
            <div className="stat-sub">
              failed claims · {fmt(fc.insideWindow)} earned a tier
            </div>
          </div>

          <div className="stat-tile">
            <div className="stat-label">Median Claim Speed</div>
            <div className={`stat-value ${loading ? 'skeleton' : ''}`}>
              {fmtSeconds(data?.claims?.medianSecondsAfterNotification)}
            </div>
            <div className="stat-sub">after the agent was notified</div>
          </div>
        </div>
      </div>

      {/* Tier distribution */}
      <div className="dashboard-card">
        <div className="card-header"><h3>Tier Distribution</h3></div>
        <p className="card-note">
          Every agent sits in exactly one tier. Higher tiers are notified earlier.
          If most agents holding priority pile up at Tier 3, the head start stops
          differentiating anyone and the tiers need finer resolution.
        </p>

        {[3, 2, 1, 0].map(tier => {
          const count = dist[tier] || 0;
          const meta = TIER_META[tier];
          const pct = totalAgents > 0 ? Math.round((count / totalAgents) * 100) : 0;
          return (
            <div key={tier}>
              <div
                className="bar-row"
                onClick={() => fetchTierMembers(tier)}
                style={{ cursor: 'pointer' }}
                title="Click to see which agents are in this tier"
              >
                <div className="bar-label">
                  {meta.label}
                  <span>{meta.sub}</span>
                </div>
                <div className="bar-track">
                  <div
                    className="bar-fill"
                    style={{
                      width: `${(count / maxTierCount) * 100}%`,
                      background: `linear-gradient(90deg, ${meta.color}, ${meta.color})`,
                    }}
                  />
                </div>
                <div className="bar-value">
                  {fmt(count)}
                  <span style={{
                    marginLeft: '6px',
                    fontSize: '10px',
                    color: '#9ca3af',
                    display: 'inline-block',
                    transform: openTier === tier ? 'rotate(90deg)' : 'none',
                    transition: 'transform 120ms'
                  }}>▶</span>
                </div>
              </div>

              {openTier === tier && (
                <div style={{
                  margin: '0 0 12px 0',
                  padding: '12px 14px',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}>
                  {tierLoading && !tierMembers[tier] && (
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>Loading…</div>
                  )}

                  {tierMembers[tier]?.length === 0 && (
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      No agents in this tier.
                    </div>
                  )}

                  {tierMembers[tier]?.map(a => (
                    <div
                      key={a._id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '7px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px'
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 600, color: '#111' }}>{a.name}</span>
                        <span style={{ color: '#9ca3af', marginLeft: '8px', fontSize: '12px' }}>
                          {a.email}
                        </span>
                        {/* A paused or inactive agent still occupies a tier but
                            won't be claiming — worth seeing here rather than
                            wondering why the tier looks busy and nothing moves. */}
                        {a.agentStatus && a.agentStatus !== 'active' && (
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '11px',
                            padding: '2px 6px',
                            borderRadius: '999px',
                            background: '#fef6e7',
                            color: '#92600a'
                          }}>
                            {a.agentStatus}
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {a.pointsBalance} pts
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {withPriority > 0 && (
          <div className={`verdict ${ceilingRate >= 60 ? 'warn' : 'ok'}`}>
            {ceilingRate >= 60
              ? `${ceilingRate}% of agents holding priority are already at the Tier 3 ceiling. The head start is no longer separating them — they're racing each other in the same window. Consider ordering within a tier by grievance age.`
              : `${ceilingRate}% of agents holding priority are at the Tier 3 ceiling. Healthy spread — agents are cycling through and landing Users.`}
          </div>
        )}
      </div>

      {/* The 5-minute window */}
      <div className="dashboard-card">
        <div className="card-header"><h3>Grievance Window · {data?.grievanceWindowMinutes ?? 5} min</h3></div>
        <p className="card-note">
          An agent earns a tier only if they attempt a claim within {data?.grievanceWindowMinutes ?? 5} minutes
          of <strong>their own</strong> notification and lose. These are the response times of agents
          who <em>lost</em>. If p75 sits above the cutoff, good-faith agents are missing it by
          seconds and earning nothing — widen the window.
        </p>

        <div className="pct-grid">
          {[
            { label: 'p50', value: p.p50 },
            { label: 'p75', value: p.p75 },
            { label: 'p90', value: p.p90 },
          ].map(row => {
            const cutoff = (data?.grievanceWindowMinutes ?? 5) * 60;
            const over = row.value != null && row.value > cutoff;
            return (
              <div className={`pct-tile ${over ? 'over' : ''}`} key={row.label}>
                <div className="pct-label">{row.label}</div>
                <div className={`pct-value ${loading ? 'skeleton' : ''}`}>
                  {fmtSeconds(row.value)}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '16px' }} className="mini-grid">
          <div className="mini-tile">
            <div className="mini-label">Earned A Tier</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>{fmt(fc.insideWindow)}</div>
          </div>
          <div className="mini-tile">
            <div className="mini-label">Missed The Window</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>{fmt(fc.outsideWindow)}</div>
          </div>
          <div className="mini-tile">
            <div className="mini-label">Inside Rate</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>
              {insideRate == null ? '—' : `${insideRate}%`}
            </div>
          </div>
        </div>

        {p.p75 != null && (
          <div className={`verdict ${p.p75 > (data?.grievanceWindowMinutes ?? 5) * 60 ? 'warn' : 'ok'}`}>
            {p.p75 > (data?.grievanceWindowMinutes ?? 5) * 60
              ? `75% of losing agents took longer than ${fmtSeconds(p.p75)} to attempt — past the ${data?.grievanceWindowMinutes ?? 5}-minute cutoff. Most of them are earning nothing despite trying. Consider widening the window.`
              : `75% of losing agents attempted within ${fmtSeconds(p.p75)} — comfortably inside the ${data?.grievanceWindowMinutes ?? 5}-minute cutoff. The window is catching the agents it's meant to catch.`}
          </div>
        )}
      </div>

      {/* Churn + delivery */}
      <div className="dashboard-card">
        <div className="card-header"><h3>Queue Churn</h3></div>
        <p className="card-note">
          Escalations are agents earning a tier. Resets are agents landing a User and
          spending it. If escalations badly outpace resets, agents are climbing but
          not converting — the head start isn't enough.
        </p>
        <div className="mini-grid">
          <div className="mini-tile">
            <div className="mini-label">Escalations</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>{fmt(data?.churn?.escalations)}</div>
          </div>
          <div className="mini-tile">
            <div className="mini-label">Resets (Claimed)</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>{fmt(data?.churn?.resets)}</div>
          </div>
          <div className="mini-tile">
            <div className="mini-label">Decays (30d Idle)</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>{fmt(data?.churn?.decays)}</div>
          </div>
          <div className="mini-tile">
            <div className="mini-label">Waves Sent</div>
            <div className={`mini-value ${loading ? 'skeleton' : ''}`}>{fmt(data?.notifications?.wavesSent)}</div>
          </div>
          <div className="mini-tile" style={
            data?.notifications?.wavesFailed > 0
              ? { background: 'rgba(220, 38, 38, 0.05)', borderColor: 'rgba(220, 38, 38, 0.2)' }
              : undefined
          }>
            <div className="mini-label">Waves Failed</div>
            <div
              className={`mini-value ${loading ? 'skeleton' : ''}`}
              style={data?.notifications?.wavesFailed > 0 ? { color: '#dc2626' } : undefined}
            >
              {fmt(data?.notifications?.wavesFailed)}
            </div>
          </div>
        </div>

        {data?.notifications?.wavesFailed > 0 && (
          <div className="verdict warn">
            {data.notifications.wavesFailed} notification wave(s) failed to POST to Zapier.
            Agents were never told about those Users. Check the webhook.
          </div>
        )}
      </div>

      {/* Claim attempt log */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Claim Attempts</h3>
          {attemptSummary && (
            <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>
              {fmt(attemptSummary.totalAttempts)} attempts · {fmt(attemptSummary.succeeded)} won ·{' '}
              {fmt(attemptSummary.failed)} lost · {fmt(attemptSummary.earnedTier)} earned a tier
            </span>
          )}
        </div>
        <p className="card-note">
          Every claim an agent has attempted. <strong>Response</strong> is measured from
          the moment <em>that agent</em> was notified — not from when the User was created.
          A loss inside {attemptSummary?.grievanceWindowMinutes ?? 5} minutes earns a priority tier;
          outside it earns nothing.
        </p>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {ATTEMPT_FILTERS.map(f => (
            <button
              key={f.key}
              className={`preset-chip ${outcomeFilter === f.key ? 'on' : ''}`}
              onClick={() => setOutcomeFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
          <span style={{ width: '1px', background: '#ececec', margin: '0 4px' }} />
          {WINDOW_FILTERS.map(f => (
            <button
              key={f.key}
              className={`preset-chip ${windowFilter === f.key ? 'on' : ''}`}
              onClick={() => setWindowFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Header */}
        <div className="attempt-row attempt-head">
          <div>Agent</div>
          <div>User</div>
          <div>Result</div>
          <div>Response</div>
          <div style={{ textAlign: 'right' }}>When</div>
        </div>

        {attemptsLoading ? (
          <div className="empty-row">Loading attempts…</div>
        ) : attempts.length === 0 ? (
          <div className="empty-row">
            No claim attempts in this window. Rows appear once an agent tries to claim a User.
          </div>
        ) : (
          <>
            {attempts.map(a => {
              const won = a.type === 'claim_success';
              const earned = !won && a.withinGrievanceWindow === true;
              const missed = !won && a.withinGrievanceWindow === false;
              return (
                <div className="attempt-row" key={a._id}>
                  <div className="attempt-agent">
                    {a.agent?.name || 'Unknown agent'}
                    <span>{a.agent?.email || ''}</span>
                  </div>
                  <div className="attempt-lead">
                    {a.lead?.name || '—'}
                    <span>
                      {a.leadTier ? a.leadTier : ''}
                      {a.pointsCost != null ? ` · ${a.pointsCost} pts` : ''}
                    </span>
                  </div>
                  <div>
                    <span className={`pill ${won ? 'won' : 'lost'}`}>
                      {won ? 'Claimed' : 'Beaten'}
                    </span>
                  </div>
                  <div className={`attempt-time ${earned ? 'earned' : missed ? 'missed' : ''}`}>
                    {fmtSeconds(a.responseSeconds)}
                    {earned && <span>earned tier</span>}
                    {missed && <span>too slow</span>}
                  </div>
                  <div className="attempt-when">
                    {new Date(a.createdAt).toLocaleString()}
                  </div>
                </div>
              );
            })}

            {attemptsHasMore && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button className="btn" onClick={() => fetchAttempts(attemptPage + 1, true)}>
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PriorityQueuePanel;